<template>
  <div class="products-showcase-section">
    <div class="showcase-container">
      <div class="content-header">
        <div class="section-badge">
          <div class="badge-line"></div>
          <span class="badge-text">EXPLORA FOURIN</span>
        </div>
        <h2 class="showcase-title">Explora Fourin Track en Detalle</h2>
        <p class="showcase-subtitle">
          Descubre cada módulo de nuestra plataforma de gestión de activos
        </p>
      </div>

      <div class="showcase-content">
        <!-- Navigation Tabs -->
        <div class="showcase-tabs">
          <button
            v-for="(module, index) in modules"
            :key="index"
            class="tab-button"
            :class="{ active: currentModule === index }"
            @click="setCurrentModule(index)"
          >
            <span class="tab-icon material-symbols-outlined">{{ module.icon }}</span>
            <span class="tab-label">{{ module.name }}</span>
          </button>
        </div>

        <!-- Image Display -->
        <div class="showcase-display" @mouseenter="stopAutoPlay" @mouseleave="startAutoPlay">
          <div class="image-container">
            <img
              :src="modules[currentModule].image"
              :alt="modules[currentModule].name"
              class="showcase-image"
              @load="onImageLoad"
            />
            <div class="image-overlay">
              <div class="overlay-content">
                <h3 class="overlay-title">{{ modules[currentModule].name }}</h3>
                <p class="overlay-description">{{ modules[currentModule].description }}</p>
              </div>
            </div>
          </div>

          <!-- Navigation Controls -->
          <div class="showcase-controls">
            <button
              class="control-btn prev-btn"
              @click="previousModule"
              :disabled="currentModule === 0"
            >
              <span class="material-symbols-outlined">chevron_left</span>
            </button>

            <div class="module-indicators">
              <span
                v-for="(module, index) in modules"
                :key="index"
                class="indicator"
                :class="{ active: currentModule === index }"
                @click="setCurrentModule(index)"
              ></span>
            </div>

            <button
              class="control-btn next-btn"
              @click="nextModule"
              :disabled="currentModule === modules.length - 1"
            >
              <span class="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import dashboardImg from '@/assets/images/dashboard3.png'
import activosImg from '@/assets/images/listaActivos.png'
import alertasImg from '@/assets/images/alertas.png'
import mantenimientosImg from '@/assets/images/mantenimientos.png'
import tiemporealImg from '@/assets/images/tiemporeal2.png'

const currentModule = ref(0)
const autoPlayTimer = ref<ReturnType<typeof setInterval> | null>(null)

const modules = [
  {
    name: 'Dashboard',
    description:
      'Panel de control principal con métricas y análisis en tiempo real de todos tus activos.',
    image: dashboardImg,
    icon: 'dashboard',
  },
  {
    name: 'Activos',
    description:
      'Gestión completa del inventario de activos físicos y patrimoniales de tu empresa.',
    image: activosImg,
    icon: 'inventory',
  },
  {
    name: 'Alertas',
    description: 'Sistema inteligente de notificaciones y alertas para mantenimiento preventivo.',
    image: alertasImg,
    icon: 'notifications',
  },
  {
    name: 'Mantenimientos',
    description:
      'Planificación, programación y seguimiento de todas las actividades de mantenimiento.',
    image: mantenimientosImg,
    icon: 'build',
  },
  {
    name: 'Tiempo Real',
    description: 'Monitoreo en vivo del estado y ubicación de todos tus activos con IoT integrado.',
    image: tiemporealImg,
    icon: 'schedule',
  },
]

const setCurrentModule = (index: number) => {
  currentModule.value = index
  resetAutoPlay()
}

const previousModule = () => {
  currentModule.value = (currentModule.value - 1 + modules.length) % modules.length
  resetAutoPlay()
}

const nextModule = () => {
  currentModule.value = (currentModule.value + 1) % modules.length
  resetAutoPlay()
}

const onImageLoad = () => {
  // Handle image load event
}

const startAutoPlay = () => {
  autoPlayTimer.value = setInterval(() => {
    currentModule.value = (currentModule.value + 1) % modules.length
  }, 5000) // Cambiar cada 5 segundos
}

const stopAutoPlay = () => {
  if (autoPlayTimer.value) {
    clearInterval(autoPlayTimer.value)
    autoPlayTimer.value = null
  }
}

const resetAutoPlay = () => {
  stopAutoPlay()
  startAutoPlay()
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowLeft') {
    previousModule()
  } else if (event.key === 'ArrowRight') {
    nextModule()
  }
}

onMounted(() => {
  startAutoPlay()
  document.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  stopAutoPlay()
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.products-showcase-section {
  padding: 80px 20px;
  background: #f8f9fa;
  min-height: 60vh;
}

.showcase-container {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.content-header {
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: center;
  margin-bottom: 60px;
}

.section-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.badge-line {
  width: 40px;
  height: 2px;
  background: #129bef;
}

.badge-text {
  color: #129bef;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.showcase-title {
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: 600;
  color: #333;
  line-height: 1.2;
  margin: 0;
}

.showcase-subtitle {
  font-size: 18px;
  line-height: 1.6;
  color: #666;
  margin: 0;
  max-width: 600px;
  margin: 0 auto;
}

.showcase-content {
  max-width: 1000px;
  margin: 0 auto;
}

/* Navigation Tabs */
.showcase-tabs {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 40px;
  flex-wrap: wrap;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.tab-button:hover {
  border-color: #129bef;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.tab-button.active {
  background: linear-gradient(135deg, #129bef 0%, #4ab7ff 100%);
  border-color: #129bef;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(18, 155, 239, 0.3);
}

.tab-icon {
  font-size: 18px;
}

.tab-label {
  font-weight: 600;
}

/* Image Display */
.showcase-display {
  position: relative;
}

.image-container {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  background: white;
  aspect-ratio: 16/9;
}

.showcase-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: all 0.3s ease;
}

.showcase-image:hover {
  transform: scale(1.02);
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 40px 30px 30px;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.image-container:hover .image-overlay {
  transform: translateY(0);
}

.overlay-content {
  color: white;
}

.overlay-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.overlay-description {
  font-size: 1rem;
  line-height: 1.5;
  opacity: 0.9;
}

/* Navigation Controls */
.showcase-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin-top: 30px;
}

.control-btn {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid #e9ecef;
  background: white;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.control-btn:hover:not(:disabled) {
  border-color: #129bef;
  color: #129bef;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.control-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.module-indicators {
  display: flex;
  gap: 8px;
  align-items: center;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #e9ecef;
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator.active {
  background: #129bef;
  transform: scale(1.3);
}

.indicator:hover {
  background: #4ab7ff;
  transform: scale(1.2);
}

@media (max-width: 768px) {
  .products-showcase-section {
    padding: 60px 15px;
  }

  .content-header {
    margin-bottom: 40px;
  }

  .showcase-title {
    font-size: 1.8rem;
  }

  .showcase-subtitle {
    font-size: 16px;
  }

  .showcase-tabs {
    gap: 8px;
    margin-bottom: 30px;
  }

  .tab-button {
    padding: 10px 16px;
    font-size: 13px;
  }

  .tab-label {
    display: none;
  }

  .tab-icon {
    font-size: 20px;
  }

  .image-overlay {
    padding: 30px 20px 20px;
  }

  .overlay-title {
    font-size: 1.3rem;
  }

  .overlay-description {
    font-size: 0.9rem;
  }

  .showcase-controls {
    gap: 20px;
  }

  .control-btn {
    width: 45px;
    height: 45px;
    font-size: 18px;
  }
}

@media (max-width: 480px) {
  .products-showcase-section {
    padding: 40px 10px;
  }

  .showcase-tabs {
    gap: 6px;
  }

  .tab-button {
    padding: 8px 12px;
  }

  .tab-icon {
    font-size: 18px;
  }

  .control-btn {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
}
</style>
